
import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components';
import { useAccount, useNetwork } from 'wagmi';
import { crates, artifacts } from './Items';
import { SingleArrowIcon } from '../../components/Icons/SingleArrowIcon';
import { ConfirmCraft } from './ConfirmCraft';
import { breakpoints, Media } from '../../styles';
import { useWidthBreakpoint } from '../../hooks';
import { CONNECTED_CHAIN } from '@/constants/chains';

interface CraftingProps {
    recipesMap: any,
    artifactBalances: any[],
    updateBalances: () => void
}

export const Crafting = ({recipesMap, artifactBalances, updateBalances}: CraftingProps) => {

    const recipes: any[] = Object.values(recipesMap).reverse().sort((a: any, b: any) => b.inputs.length - a.inputs.length);

    const { address: walletAddress, isConnected } = useAccount()
    const { chain: activeChain } = useNetwork();
    const validChain = activeChain?.name == CONNECTED_CHAIN

    const isSmallDesktop = useWidthBreakpoint(breakpoints.smallDesktop)
    const isMobile = useWidthBreakpoint(breakpoints.mobile)

    const [craftAmounts, setCraftAmounts] = useState<Record<number, number>>({});
    const [showMax, setShowMax] = useState<{[i: number] : boolean}>({});
    const [showClear, setShowClear] = useState<{[tier: number] : boolean}>({});
    const [craftDisabled, setCraftDisabled] = useState<boolean[]>(recipes.map(() => true));
    const [confirmVisible, setConfirmVisible] = useState(false);

    const [confirmBurnAmounts, setConfirmBurnAmounts] = useState<Record<string, number>>({});
    const [confirmRecipeIndex, setConfirmRecipeIndex] = useState<number>(-1)
    const [confirmCraftAmount, setConfirmCraftAmount] = useState<number>(-1)

    const artifactNames = artifacts.map((artifact) => artifact.name)

    const recipeMaxes = recipes.map((recipe : any) => {

        const limits = recipe.inputs.map((input : any, index : number) => {
            input.balance = artifactBalances[artifactNames.indexOf(input.name)]
            return Math.floor(input.balance / recipe.inputAmounts[index])
        })
        return Math.min(...limits)
    })

    const handleCraftInput = (value : string, resultID : number) => {

        const newCraftAmounts = {...craftAmounts}
        value = value.replace(/\D/g, '');

        if(value){ // Value is not empty and is a positive integer
            if (value == '0')
                delete newCraftAmounts[resultID]
            else 
                newCraftAmounts[resultID] = parseInt(value);
        } else {
            delete newCraftAmounts[resultID]
        }
        setCraftAmounts(newCraftAmounts)

    }

    const showCraftMax = (resultID : number, visible : boolean) => {
        const newShowMax = {...showMax}
        newShowMax[resultID] = visible;
        setShowMax(newShowMax)
    }

    const showCraftClear = (resultID : number, visible : boolean) => {
        const newShowClear = {...showClear}
        newShowClear[resultID] = visible;
        setShowClear(newShowClear)
    }

    useEffect(() => {
        if(isConnected && validChain){
            const newCraftDisabled = recipes.map((_: any, index: number) => {
                if(craftAmounts[index]){
                    return craftAmounts[index] > recipeMaxes[index]
                } else {
                    return true
                }
            })
            setCraftDisabled(newCraftDisabled)
        } else {
            setCraftDisabled(recipes.map(() => true))
        }   
    }, [craftAmounts, walletAddress, validChain, recipesMap])

    return (
        <>
            {recipes.length == 0 && <Title>No Recipes Available</Title> }
            <Column style={{width: '100%', rowGap: '12px'}}>
                {recipes.map((recipe: any, recipeIndex: number) => {
                    const craftNum = craftAmounts[recipeIndex] ?? 0
                    const resultArtifact = recipe.outputs[0]
                    return (
                        <Container key={recipeIndex}>
                            {recipe.inputs.map((input : any, index : number) => {
                                const totalCost = recipe.inputAmounts[index] * Math.max(craftNum, 1)
                                const disabled = totalCost > input.balance
                                const svgColor = disabled ? '#787b7ef9' : '#00BDFF'
                                return (
                                    <div style={{display: 'flex'}} key={index}>
                                        <Column>
                                            <Artifact src={`../../assets/booty/${input.image}.jpg`} disabled={disabled}/>
                                            <Row>
                                                <svg fill={svgColor} xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" version="1.1" x="0px" y="0px" width="14px" height="14px" viewBox="0 0 85.311 100" enableBackground="new 0 0 85.311 100" xmlSpace="preserve" style={{marginTop: '8px'}}>
                                                    <path d="M67.83,64.129c0.028-2.929,0.017-9.357-0.493-15.71c0.045,2.677,0.115,5.127,0.206,6.409  C67.594,55.563,67.706,59.413,67.83,64.129z"/>
                                                    <path d="M43.334,9.037c0.087-2.058-0.024-4.262,1.72-4.136c0.837,0.061,2.279,0.274,4.063,0.631  c1.978,0.396,1.699,11.121,1.699,11.121l7.171,2.788c0.236-7.761-1.704-16.773-3.64-17.325c-3.244-0.925-8.005-2.854-12.736-1.819  c-3.161,0.691-4.007,12.296-4.007,12.296l5.532,1.449C43.136,14.042,43.194,12.33,43.334,9.037z"/>
                                                    <path d="M9.969,48.267c1.165,0.431,3.085,1.064,5.358,1.777c1.699-4.705,4.58-8.294,4.58-8.294  s0.665-1.743,4.006-1.041c3.341,0.701,3.497,2.226,3.497,2.226s-2.164,3.508-4.268,8.236c-0.13,0.292-0.301,0.655-0.431,1.102  c5.297,1.544,10.415,2.911,11.887,3.045c2.897,0.265,10.174,0.583,11.557-6.42c1.364-6.911,2.714-11.127,3.864-13.759  c0.23-0.575,0.437-1.051,0.589-1.395c0.313-0.708,1.266-2.128,2.652-3.52c0-0.001,0-0.001,0-0.002  c0.004-0.01,0.01-0.015,0.018-0.017c1.852-1.85,4.472-3.642,7.397-3.631c0.154,0,0.342,0.019,0.551,0.051  c-1.721-1.914-3.682-3.544-5.77-4.349c-0.475-0.183-17.718-6.998-22.87-6.269c-0.148,0.021-0.286,0.041-0.419,0.06  c0.25-0.036,0.419-0.06,0.419-0.06S20.775,16.3,12.965,24.104c-4.08,4.076-7.662,11.388-9.911,18.414  C3.025,44.301,5.3,46.541,9.969,48.267z"/>
                                                    <path d="M33.018,60.553c-2.322-0.426-6.635-1.608-11.232-2.967c-0.7,2.769-0.105,7.525-0.105,7.525  s-0.904,2.158-4.467,1.369c-3.036-0.674-2.958-2.865-2.958-2.865s-0.776-4.446-0.092-8.359c-4.376-1.378-8.094-2.615-9.462-3.14  c-1.646-0.63-2.833-1.508-3.688-2.489c-0.063,0.389-0.119,1.96-0.172,2.334C-2.26,74.053,4.178,83.567,4.634,85.576  c0.613,2.701,12.381,8.364,23.741,12.513c6.59,2.407,13.947,1.94,16.869,1.73l0.214-0.012c0.465-8.542,1.731-30.777,2.352-44.384  C46.826,60.419,38.577,61.571,33.018,60.553z"/>
                                                    <path d="M67.83,64.129c-0.124-4.716-0.235-8.564-0.287-9.303c-0.091-1.28-0.161-3.73-0.206-6.408  c-0.455-5.656-1.913-11.727-3.16-14.882c-2.096-5.312-8.183,1.351-9.902,5.546c-1.78,4.344-3.465,54.419-3.629,59.383  c4.25-1.107,10.338-2.696,11.084-3.867c1.581-2.476,2.438-4.873,2.833-6.914c0.262-1.359,3.777-2.63,3.777-2.63  s-0.249-10.8-0.474-19.516l-0.062,0.23C67.804,65.77,67.821,65.169,67.83,64.129z"/>
                                                    <path d="M83.415,42.354c0,0-0.901-10.732-1.105-12.818c-0.494-5.037-5.038-21.237-20.526-8.825l4.103,4.476  c0,0,10.496-9.432,10.892,13.042c0.033,1.902,0.159,4.559,1.303,20.549c1.266,17.679-5.648,17.777-5.648,17.777l0.312,7.518  c0,0,2.568-0.672,5.22-1.788c3.753-1.581,7.349-8.198,7.349-17.897C85.311,60.55,83.415,42.354,83.415,42.354z"/>
                                                    <path d="M29.176,12.348l3.424-0.526V8.924l0.659-2.37c0,0-3.162,1.449-4.083,2.897  c-0.922,1.449-1.318,2.239-1.318,2.897C27.858,13.007,29.176,12.348,29.176,12.348z"/>
                                                </svg>
                                                <BalanceCost style={{marginTop: '8px'}} disabled={disabled}>{`${input.balance} | ${totalCost}`}</BalanceCost>
                                                <svg fill={svgColor} version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" width="14px" height="14px" viewBox="0 0 949.984 949.984" xmlSpace="preserve" style={{marginTop: '8px'}}>
                                                    <g>
                                                        <g>
                                                            <path d="M623.393,949.092c13.899,0,25.1-11.299,25.1-25.1v-49.799c-55,19.699-113.6,30.1-173.5,30.1s-118.6-10.301-173.5-30.1
                                                                v49.799c0,13.9,11.3,25.1,25.1,25.1H623.393z"/>
                                                            <path d="M882.292,115.292c-40.6-30.2-98-21.8-128.3,18.8l-180.6,242.7h228.6l99.1-133.2
                                                                C931.292,202.993,922.893,145.593,882.292,115.292z"/>
                                                            <path d="M310.292,849.492c51.3,18.5,106.8,28.5,164.7,28.5s113.4-10,164.7-28.5c168.899-60.699,292.899-212.699,310.1-398.599
                                                                c2.4-25.7-18-47.9-43.8-47.9h-123.6h-228.5h-509.9c-25.8,0-46.2,22.2-43.8,47.9C17.392,636.793,141.392,788.793,310.292,849.492z"
                                                                />
                                                            <polygon points="155.692,239.893 190.292,155.693 279.092,116.792 189.592,82.693 155.692,0.893 121.692,82.693 32.192,116.792 
                                                                120.992,155.693 		"/>
                                                        </g>
                                                    </g>
                                                </svg>
                                            </Row>
                                        </Column>
                                        {index < recipe.inputs.length - 1 && <Sign style={{margin: `${isSmallDesktop ? '12px 16px' : '32px 32px'}`}}>+</Sign>}
                                    </div>
                                )
                            })}
                            {!isMobile && 
                                <Sign>
                                    <SingleArrowIcon/>
                                </Sign>
                            }
                            <Row style={{marginTop: `${isMobile ? 32 : 0}px`}}>
                                <Column>
                                    <Artifact src={`../../assets/booty/${resultArtifact.image}.jpg`}/>
                                    <CraftInputRow>
                                        <Row onMouseLeave={() => showCraftClear(recipeIndex, false)}>
                                            {/* <EdgeButton 
                                                style={{marginLeft: '-48px', position: 'absolute'}} 
                                                visible={showClear[resultID] && craftNum != 0}
                                                onClick={() => handleCraftInput('0', resultID)}
                                            >Clear</EdgeButton> */}
                                            <AddSubButton 
                                                // style={{marginRight: '8px'}} 
                                                disabled={craftNum == 0}   
                                                onClick={() => handleCraftInput(craftNum > 1 ? (craftNum - 1).toString() : '', recipeIndex)}
                                                onMouseOver={() => showCraftClear(recipeIndex, true)}
                                            >-</AddSubButton>
                                        </Row>
                                        <CraftInput 
                                            placeholder='0' 
                                            value={craftAmounts[recipeIndex] > 0 ? craftAmounts[recipeIndex].toString() : ''} 
                                            onChange={((e) => handleCraftInput(e.target.value, recipeIndex))}
                                            onFocus={(e) => e.target.placeholder = ''}
                                            onBlur={(e) => e.target.placeholder = '0'}
                                            // disabled={!crateEligibility[index] || crateSupply[index] == '0'}
                                            onMouseOver={() => {
                                                showCraftMax(recipeIndex, false)
                                                showCraftClear(recipeIndex, false)
                                            }}
                                            inputMode={'numeric'}
                                        />
                                        <Row>
                                            <AddSubButton 
                                                // style={{marginLeft: '8px'}} 
                                                // disabled={crateSupply[index] == '0' || mintNum == crate.maxMint || !crateEligibility[index]} 
                                                onClick={() => handleCraftInput((craftNum + 1).toString(), recipeIndex)}
                                                onMouseOver={() => showCraftMax(recipeIndex, true)}
                                            >+</AddSubButton>
                                            {/* <EdgeButton 
                                                style={{marginLeft: '42px', position: 'absolute'}} 
                                                visible={showMax[resultID]}
                                                onClick={() => handleCraftInput(recipeMaxes[recipeIndex].toString(), resultID)}
                                                onMouseLeave={() => showCraftMax(resultID, false)}
                                            >Max</EdgeButton> */}
                                        </Row>
                                    </CraftInputRow>
                                </Column>
                                <Column style={{width: '128px', marginLeft: `${isSmallDesktop ? isMobile ? 24 : 32 : 48}px`}}>
                                    <div style={{display: 'flex', flexDirection: 'column'}}>
                                        <ArtifactName textLength={resultArtifact.name.length}>{resultArtifact.name}</ArtifactName>
                                        <ArtifactDesc>{resultArtifact.desc}</ArtifactDesc>
                                        {
                                        'effect' in resultArtifact && resultArtifact['effect']?.type == 'Booster' && 
                                        <Row style={{display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '8px'}}>
                                            <Value style={{lineHeight: '12px', fontSize: '12px'}}>{`${resultArtifact.effect?.value == 0 ? '???' : resultArtifact.effect?.value + '%'}`}</Value>        
                                            <img style={{width: '24px', margin: '0 4px'}} src={`../../assets/icons/boost.svg`}/>
                                            <Value style={{lineHeight: '12px', fontSize: '12px'}}>{`${resultArtifact.effect?.duration}d`}</Value>
                                        </Row> 
                                        }
                                    </div>
                                    <ExecuteButton disabled={craftDisabled[recipeIndex]} onClick={() => {
                                        const newCraftDisabled = {...craftDisabled}
                                        newCraftDisabled[recipeIndex] = true
                                        setCraftDisabled(newCraftDisabled)
                                        const burnAmounts : Record<string, number> = {}
                                        recipes[recipeIndex].inputs.forEach((input : any, index: number) => {
                                            const totalCost = recipe.inputAmounts[index] * (craftNum ?? 1)
                                            burnAmounts[input.image] = totalCost
                                        })
                                        setConfirmBurnAmounts(burnAmounts)
                                        setConfirmRecipeIndex(recipeIndex)
                                        setConfirmCraftAmount(craftNum)
                                        setConfirmVisible(true)
                                    }}>Craft</ExecuteButton>
                                </Column>
                            </Row>

                        </Container>
                    )
                })}
            </Column>
            <ConfirmCraft 
                visible={confirmVisible} 
                setVisible={setConfirmVisible} 
                burnAmounts={confirmBurnAmounts} 
                recipe={recipes[confirmRecipeIndex]}
                recipeID={parseInt(Object.keys(recipesMap).find((index) => recipesMap[index] == recipes[confirmRecipeIndex]) ?? '0')}
                craftAmount={confirmCraftAmount} 
                setCraftDisabled={(disabled: boolean) => {
                    const newCraftDisabled = {...craftDisabled}
                    newCraftDisabled[confirmRecipeIndex] = disabled
                    setCraftDisabled(newCraftDisabled)
                }}
                update={() => {
                    updateBalances()
                    const newCraftAmounts = {...craftAmounts}
                    delete newCraftAmounts[confirmRecipeIndex]
                    setCraftAmounts(newCraftAmounts)
                }}
            />
        </>
    )
}

const Title = styled.h3`
  font-weight: 500;
  font-size: 30px;
  line-height: 122px;
  text-align: center;
  letter-spacing: -0.03em;
  color: #ffffff;
`

const IconContainer = styled.div`
  width: 32px;
  > img {
    width: 32px;
  }
  color: #00BDFF;
  font-size: 24px;
  margin: auto 4px auto 4px;
  vertical-align: center;
`

const BalanceCost = styled.p<{disabled: boolean}>`
  font-size: 30px;
  text-align: center;
  letter-spacing: -0.03em;
  color: #ffffff;
  margin-left: 8px;
  margin-right: 8px;

  ${({ disabled }) => disabled && css`
      color: #787b7ef9;
  `};

  ${Media.smallDesktop}{
    font-size: 24px;
  }

  ${Media.smallMobile}{
    font-size: 22px;
  }
`;

const Column = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`;

const Container = styled.div`
  width: 100%;
  height: fit-content;     

  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  padding: 12px;
  background: #151530;
  border: 1px solid #1E2239;
  border-radius: 16px;
`;

const Artifact = styled.img<{disabled? : boolean}>`
  width: 136px;  
  background: #151530;
  border: 2px solid #1E2239;
  border-radius: 16px;

  ${({ disabled }) => disabled && css`
    filter: grayscale(100%);
  `};

  ${Media.smallDesktop}{
    width: 96px;
  }
`;

const ArtifactName = styled.p<{textLength? : number}>`
  font-weight: 500;
  font-size: 16px;
  color: #00BDFF;
  margin-top: 30px;
  margin-bottom: 8px;
  text-align: center;

  ${({ textLength }) => textLength && textLength > 18 && css`
    font-size: 13px;
  `};

  ${Media.smallDesktop}{
    margin-top: 16px;
  }

  ${Media.mobile}{
    margin-top: 0;
  }
`

const ArtifactDesc = styled.p`
  font-weight: 250;
  font-size: 11px;
  line-height: 13px;
  text-align: center;
  padding: 0 6px;
`;

const Value = styled.p`
  font-size: 16px;
  line-height: 17px;
  text-align: left;
  font-weight: 100;
  line-height: 24px;
  color: #00BDFF;
  white-space: nowrap;
  letter-spacing: -0.02em;
`

const Sign = styled.div`
  font-weight: 10;
  font-size: 64px;
  text-align: center;
  color: #00BDFF;

  svg {
    width: 64px;

    margin: 0px 32px 28px 32px;

    ${Media.smallDesktop}{
        margin: 0px 16px 22px 16px;
    }
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CraftInputRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  margin-top: 10px;
`;

const inputTextStyles = css`
  font-size: 30px;
`

const placeholderStyles = css`
  ${inputTextStyles}
`

export const CraftInput = styled.input`
  width: 48px;
  color: #FFFFFF;
  text-align: center;
  border: none;
  background: transparent;
  outline: none;
  font-weight: 500;
  height: 30px;
  margin: 0 8px;

  ${inputTextStyles}

  &:focus {
    border-color: #FFFFFF;
  }

  &::-webkit-input-placeholder {
    ${placeholderStyles}
  }
  &::-moz-placeholder {
    ${placeholderStyles}
  }
  &:-ms-input-placeholder {
    ${placeholderStyles}
  }
  &:-moz-placeholder {
    ${placeholderStyles}
  }

  ${Media.tablet}{
    font-size: 24px;
    margin: 2px 4px 0 4px;
  }
`;

const AddSubButton = styled.button`
  border: none;
  border: solid 1px #00BDFF;
  border-radius: 6px;
  padding: 0;
  background: rgba(255, 255, 255,0);
  color: white;
  font-size: 16px;
  font-weight: 500;
  height: 28px;
  width: 28px;

  &:not(:disabled):hover {
    background: linear-gradient(90.44deg, #37dcf2 0.87%, #07c0fb 100%);
    color: #000E47;
    border: solid 2px #000E47;
  }

  &:disabled {
    border: solid 1px #464659;
    color: #464659;
    pointer-events: none;
  }
`;

const EdgeButton = styled.button<{visible?: boolean}>`

  border: none;
  border: solid 1px #00BDFF;
  border-radius: 6px;
  padding: 0;
  background: rgba(255, 255, 255,0);
  color: white;
  font-size: 12px;
  font-weight: 1;
  height: 28px;
  width: 42px;
  visibility: hidden;

  ${({ visible }) => visible && css`
      visibility: visible;
  `};

  :hover {
    background: linear-gradient(90.44deg, #37dcf2 0.87%, #07c0fb 100%);
    color: #000E47;
    border: solid 2px #000E47;
  }
`;

const TVL = styled.p`
  font-weight: 500;
  font-size: 24px;
  text-align: center;
  letter-spacing: -0.03em;
  color: #ffffff;
`

const ExecuteButton = styled.button`
  border: none;
  margin-top: 22px;
  background: linear-gradient(90.44deg, #37dcf2 0.87%, #07c0fb 100%);
  color: #0a0e27;
  font-size: 16px;
  font-weight: 500;
  min-height: 42px;

  width: 100%;
  padding: 4px 0px 4px 0px;
  border-radius: 16px;

  &:disabled {
    background: #0a0e27;
    color: #464659;
    border: solid 2px #464659;
  }

  ${Media.smallDesktop}{
    margin-top: 16px;
  }

  ${Media.mobile}{
    margin-top: 8px;
  }
`;